'use client';

import AdvisorSearchFilter, { SelectedFilters } from '@components/AdvisorSearchFilter';
import AdvisorCardGrid from '@components/CardGridWithPagination/AdvisorCardGrid';
import InteriorHero from '@components/Hero/InteriorHero';
import { Block } from '@components/Layout';
import ListCount from '@components/ListCount';
import LoadingSpinner from '@components/LoadingSpinner';
import NullResultsSearchListings from '@components/NullResults/SearchListings';
import { Pagination } from '@components/Pagination';
import Select from '@components/Select';
import { query } from '@design/templateFns';
import { Magnifier } from '@icon/Magnifier';
import evAPI from '@util/api/ev';
import {
  expandedLanguagesFilterData,
  generateResults,
  mapResults,
  searchFilterData,
} from '@util/api/listingAdvisors';
import { SearchResultType } from '@util/constants';
import { objectToQueryParams } from '@util/queryParams/objectToQueryParams';
import { queryParamsToObject } from '@util/queryParams/queryParamsToObject';
import { useTranslations } from 'next-intl';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { FC, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import {
  AdvisorContainer,
  AdvisorFilterWrapper,
  AdvisorHeader,
  AdvisorHeaderContainer,
  AdvisorListWrapper,
  Headline,
  InteriorHeroWrapper,
  PaginationContainer,
  SearchbarWrapper,
  SearchButton,
  SearchInput,
  SearchInputWrapper,
} from './styles';

const PAGE_SIZE = 18;
const FIRST_PAGE = 1;

interface AdvisorSearchPageProps {
  interiorHero: any;
  shopIds: string | null;
  shopName: string | null;
  shopData?: any | null;
}

const AdvisorSearchPage: FC<AdvisorSearchPageProps> = ({ interiorHero, shopIds, shopName, shopData }) => {
  const t = useTranslations();
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const router = useRouter();
  const api = new evAPI();
  const shopLocation = [shopData?.name, shopData?.state].filter(Boolean).join(', ');
  const [searchInput, setSearchInput] = useState('');
  const [fetchAdvisorResult, setFetchAdvisorResult] = useState<SearchResultType>(
    SearchResultType.UNKNOWN,
  );
  const [sortBy, setSortBy] = useState({
    title: t('First Name (A-Z)'),
    value: 'firstNameASC',
  });
  const [mappedResults, setMappedResults] = useState({
    advisors: [],
    count: 0,
    total: 0,
  });
  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageSize: PAGE_SIZE,
    totalPages: Infinity,
  });
  const [applicableLanguageFilters, setApplicableLanguageFilters] = useState(
    expandedLanguagesFilterData,
  );
  useEffect(() => {
    if (searchInput === '') {
      void getAdvisors({}, FIRST_PAGE);
    }
  }, [searchInput]);

  const sortByObj = useMemo(() => {
    return {
      sortDir: sortBy.value === 'firstNameASC' || sortBy.value === 'lastNameASC' ? 'asc' : 'desc',
      sortKey:
        sortBy.value === 'firstNameASC' || sortBy.value === 'firstNameDESC'
          ? 'firstname'
          : 'lastname',
    };
  }, [sortBy]);

  const getAdvisors = async (selectedFilters: SelectedFilters, currentPage: number) => {
    setFetchAdvisorResult(SearchResultType.FETCHING);
    let filters = getFilterParams(selectedFilters);
    if (filters) {
      filters = {
        ...filters,
        currentPage: currentPage.toString(),
      };
      updateQueryParams(filters);
    } else {
      updateQueryParams({ currentPage: currentPage.toString() });
    }

    // Add shopIds to filters, but only after the updateQueryParams so that the shopIds are not included in the URL
    filters.shopIds = shopIds;

    const offset = currentPage === FIRST_PAGE ? currentPage - 1 : pagination.currentPage - 1;
    const results = await generateResults({
      sortType: sortByObj,
      offset: offset * pagination.pageSize,
      limit: pagination.pageSize,
      api,
      name: searchInput,
      filters,
      searchInputOverride: true,
    });
    if (results.records.length > 0) {
      updateLanguageFilters(results.languages);
    }

    const mapped = await mapResults(results.records, shopIds, shopName);
    setMappedResults({
      ...mappedResults,
      advisors: mapped,
      count:
        (mappedResults.count === 0 || results.totalRecords === mappedResults.total) &&
        PAGE_SIZE < results.totalRecords
          ? PAGE_SIZE / 2
          : results.totalRecords,
      total: results.totalRecords,
    });
    setPagination({
      ...pagination,
      currentPage: currentPage === FIRST_PAGE ? currentPage : pagination.currentPage,
      totalPages: Math.ceil(results.totalRecords / pagination.pageSize),
    });
    if (mapped?.length === 0) {
      setFetchAdvisorResult(SearchResultType.EMPTY);
    } else {
      setFetchAdvisorResult(SearchResultType.COMPLETED);
    }
  };

  const getFilterParams = (selectedFilters: SelectedFilters): any => {
    let filters = {};
    const languages = searchParams.get('languages');
    if (selectedFilters?.languages || languages) {
      filters = {
        ...filters,
        languages:
          selectedFilters?.languages && Array.isArray(selectedFilters?.languages)
            ? selectedFilters?.languages.join(',')
            : languages
            ? languages
            : '',
      };
    }

    const specializations = searchParams.get('specializations');
    if (selectedFilters?.specializations || specializations) {
      filters = {
        ...filters,
        specializations:
          selectedFilters?.specializations && Array.isArray(selectedFilters?.specializations)
            ? selectedFilters?.specializations.join(',')
            : specializations
            ? specializations
            : '',
      };
    }

    const roles = searchParams.get('roles');
    if (selectedFilters?.roles || roles) {
      filters = {
        ...filters,
        roles:
          selectedFilters?.roles && Array.isArray(selectedFilters?.roles)
            ? selectedFilters?.roles.join(',')
            : roles
            ? roles
            : '',
      };
    }

    return filters;
  };

  const updateQueryParams = (selectedFilters: SelectedFilters) => {
    if (searchParams.size > 0) {
      const currentParams = queryParamsToObject(searchParams.toString());
      selectedFilters = { ...currentParams, ...selectedFilters };
    }

    const queryParams = objectToQueryParams(selectedFilters);
    router.push(pathname + '?' + queryParams);
  };

  const handleSearchBarInput = (i: string) => {
    setSearchInput(i);
  };

  const handleSearchClick = () => {
    getAdvisors({}, FIRST_PAGE);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      getAdvisors({}, FIRST_PAGE);
    }
  };

  const onApplyFilter = ({ selectedFilters }) => {
    getAdvisors(selectedFilters, FIRST_PAGE);
  };
  const onClearFilter = () => {};

  const updateLanguageFilters = (records: string[]) => {
    let languagesFromAdvisors: { value: string; title: string }[] = [];
    for (const r of records) {
      const lang = expandedLanguagesFilterData.find((l) => l.value === r);
      if (lang) {
        languagesFromAdvisors.push(lang);
      }
    }

    setApplicableLanguageFilters(languagesFromAdvisors);
  };

  useEffect(() => {
    const currPage = searchParams.get('currentPage');
    let currentPageStr = Array.isArray(currPage) ? currPage[0] : currPage;
    const currentPageFromUrl = parseInt(currentPageStr, 10);
    const currentPage = isNaN(currentPageFromUrl) ? FIRST_PAGE : currentPageFromUrl;
    setPagination({ ...pagination, currentPage: currentPage });
  }, []);

  useEffect(() => {
    if (pagination.currentPage !== null) {
      getAdvisors({}, pagination.currentPage);
    }
  }, [pagination.currentPage, sortBy]);

  return (
    <>
      <InteriorHeroWrapper>
        {interiorHero?.items[0]?.fields?.header && (
          <InteriorHero header={interiorHero.items[0].fields.header} backgroundColor />
        )}
      </InteriorHeroWrapper>
      <SearchbarWrapper>
        <SearchInputWrapper>
          <SearchInput
            $active={searchInput !== ''}
            placeholder={t("Advisor's Name")}
            onChange={(e) => handleSearchBarInput(e.target.value)}
            onKeyDown={handleKeyDown}
            value={searchInput}
            type="search"
          />
          <SearchButton data-testid="search-btn" onClick={handleSearchClick}>
            <span className="visually-hidden">{t('Search')}</span>
            <Magnifier color="#fff" size="18px" />
          </SearchButton>
        </SearchInputWrapper>
        <AdvisorFilterWrapper>
          <AdvisorSearchFilter
            specializations={searchFilterData.specializations}
            languages={applicableLanguageFilters.sort((a, b) => {
              return a.title > b.title ? 1 : -1;
            })}
            roles={searchFilterData.roles}
            onApply={onApplyFilter}
            onClear={onClearFilter}
          />
        </AdvisorFilterWrapper>
      </SearchbarWrapper>
      {fetchAdvisorResult === SearchResultType.FETCHING && <LoadingSpinner />}
      {fetchAdvisorResult === SearchResultType.COMPLETED && (
        <AdvisorListWrapper>
          {shopName && shopIds && shopData
            ? <Headline>{t('Expert Advisors at Your Service in', { shopLocation })}</Headline>
            : <Headline>{t('Meet our Advisors')}</Headline>}
          <AdvisorHeaderContainer>
            <AdvisorHeader>
              <ListCount
                currentPage={pagination.currentPage}
                pageSize={mappedResults?.advisors?.length}
                totalRecords={mappedResults?.total}
                type={t('Advisors')}
              />
              <OverWriteSelect>
                <Select
                  options={[
                    {
                      title: t('First Name (A-Z)'),
                      value: 'firstNameASC',
                    },
                    {
                      title: t('First Name (Z-A)'),
                      value: 'firstNameDESC',
                    },
                    {
                      title: t('Last Name (A-Z)'),
                      value: 'lastNameASC',
                    },
                    {
                      title: t('Last Name (Z-A)'),
                      value: 'lastNameDESC',
                    },
                  ]}
                  selectedItem={sortBy}
                  onSelect={(value) => setSortBy(value)}
                  title={t('Sort By:')}
                  label={'Sort By'}
                  isSort
                />
              </OverWriteSelect>
            </AdvisorHeader>
          </AdvisorHeaderContainer>
          <AdvisorContainer>
            <AdvisorCardGrid cardsPerPage={PAGE_SIZE} cardArr={mappedResults?.advisors} />
          </AdvisorContainer>
          {pagination.totalPages > 1 && (
            <PaginationContainer>
              <Pagination
                currentPage={pagination.currentPage}
                totalPages={pagination.totalPages}
                pageSelected={(p) => {
                  setPagination({ ...pagination, currentPage: parseInt(p) });
                }}
              />
            </PaginationContainer>
          )}
        </AdvisorListWrapper>
      )}
      {fetchAdvisorResult === SearchResultType.EMPTY && (
        <Block>
          <NullResultsSearchListings
            isSVG={false}
            header={t('noResultsHeader')}
            bodyText={t('noResultsContactShopOrAdvisor')}
            onBeginNewSearchBtnClicked={() => {
              setSearchInput('');
            }}
          />
        </Block>
      )}
    </>
  );
};

export default AdvisorSearchPage;

export const OverWriteSelect = styled.div`
  div:nth-child(2) {
    @media screen and (${query.below('tablet')}) {
      position: fixed;
      bottom: 0;
      top: inherit;
      max-width: inherit;
      min-width: inherit;

      ul {
        position: initial;

        li {
          text-align: center;
          max-width: inherit;
          justify-content: center;
        }
      }
    }
  }
`;
